/** @format */

import React, { FunctionComponent, useEffect, useState } from 'react';
import { Dialog, DialogActions, DialogContent, DialogTitle } from '@material-ui/core';
import GridContainer from '../../../components/Grid/GridContainer';
import GridItem from '../../../components/Grid/GridItem';
import I18n from '../../../components/Translate/I18n';
import CustomTextButtom from '../../../components/Buttons/CustomTextButton';
import { useDispatch, useSelector } from 'react-redux';
import {
    PlatformIccidMsisdnIpPoolReducerAction,
    PlatformIccidMsisdnIpPoolReducerType,
} from '../../../modules/iccid/PlatformIccidMsisdnIpPoolReducer';
import CustomInputExt from '../../../components/CustomInput/CustomInputExt';
import { SimpleSelect } from '../../../components/Adescom/SimpleSelect';
import { ItemLoaders } from '../../../core/loaders/ItemLoaders';
import { FormGeneralHelper } from '../../../helpers/FormGeneral';
import { PlatformImsiRangeResponse } from '../../../interfaces/platform/PlatformImsiRange';
import Filter from '../../../interfaces/forms/Filter';
import { usePlatformResellerHeaders, usePlatformResellerList } from '../../../modules/lib/hooks/usePlatformResellerHooks';
import {
    apiShortPlatformMsisdnRange,
    PlatformMsisdnRange,
    PlatformMsisdnRangeResponse,
    PlatformMsisdnRangeResponseCollectionInterface,
} from '../../../interfaces/collections/PlatformMsisdnRange';
import { NotificationHelper } from '../../../helpers/NotificationHelper';

type MsisdnChangeOwnerType = {};

export const MsisdnChangeOwner: FunctionComponent<MsisdnChangeOwnerType> = (): React.ReactElement => {
    const { openMsisdnChangeOwner, valueChange, selectedValue } = useSelector(
        (state: { platformIccidMsisdnIpPoolReducer: PlatformIccidMsisdnIpPoolReducerType }) => state.platformIccidMsisdnIpPoolReducer
    );
    const [open, setOpen] = useState(false);
    const [fromNumber, setFromNumber] = useState<string | null>(null);
    const [toNumber, setToNumber] = useState<string | null>(null);
    const [totalCard, setTotalCard] = useState(0);
    const dispatch = useDispatch();

    const platformResellerList = usePlatformResellerList();
    const platformResellerHeader = usePlatformResellerHeaders(platformResellerList);

    useEffect(() => {
        setOpen(openMsisdnChangeOwner ?? false);
    }, [openMsisdnChangeOwner]);

    useEffect(() => {
        if ((fromNumber && fromNumber.length > 0) || (toNumber && toNumber.length > 0)) {
            const itemLoader = new ItemLoaders(dispatch);
            const filters: Array<Filter> = [];
            if (fromNumber && fromNumber.length > 0) {
                filters.push({ name: 'fromNumber', value: fromNumber });
            }
            if (toNumber && toNumber.length > 0) {
                filters.push({ name: 'toNumber', value: toNumber });
            }
            itemLoader.getListWithCallback(
                FormGeneralHelper.getByUri(FormGeneralHelper.getPath('/' + apiShortPlatformMsisdnRange)),
                PlatformIccidMsisdnIpPoolReducerAction.RMO_CHANGE_OPEN_DIALOG_ADVANCED_RANGE_COUNTER,
                filters,
                [],
                (data: PlatformMsisdnRangeResponseCollectionInterface): void => {
                    setTotalCard(data['hydra:member'][0].rangeCount);
                },
                { propagateDataToCallback: true, dispatchAfterCallback: false }
            );
        }
    }, [dispatch, fromNumber, toNumber]);

    const closeCallback = (): void => {
        dispatch({
            type: PlatformIccidMsisdnIpPoolReducerAction.MSISDN_CHANGE_OWNER_OPEN_DIALOG_ADVANCED,
            openMsisdnChangeOwner: false,
        } as PlatformIccidMsisdnIpPoolReducerType);
    };

    const confirmCallback = (): void => {
        if (!fromNumber || fromNumber.length !== 11) {
            NotificationHelper.notifyError('pools:sim.range_from_msisdn_empty_from_number', dispatch);
            return;
        }
        if (!toNumber || toNumber.length !== 11) {
            NotificationHelper.notifyError('pools:sim.range_from_msisdn_empty_to_number', dispatch);
            return;
        }
        const itemLoader = new ItemLoaders(dispatch);
        itemLoader.putWithCallback(
            FormGeneralHelper.getByUri(FormGeneralHelper.getPath('/' + apiShortPlatformMsisdnRange + '/-1')),
            'change_owner_call_api',
            {
                fromNumber: fromNumber,
                toNumber: toNumber ?? fromNumber,
                ownerPlatformResellerId: selectedValue,
            } as PlatformMsisdnRange,
            (data: PlatformMsisdnRangeResponse) => {
                dispatch({ type: PlatformIccidMsisdnIpPoolReducerAction.MSISDN_CHANGE_OWNER_RELOAD });
                closeCallback();
            },
            { propagateDataToCallback: true, dispatchAfterCallback: false }
        );
    };

    return (
        <Dialog open={open} onClose={closeCallback} aria-labelledby="alert-dialog-title" aria-describedby="alert-dialog-description">
            <DialogTitle id="alert-dialog-title">
                <I18n trans="pools:sim.owner_change" />
            </DialogTitle>
            <DialogContent>
                <GridContainer>
                    <GridItem xs={6}>
                        <CustomInputExt
                            labelText="pools:sim.range_from_msisdn"
                            onChange={(event: React.FormEvent<HTMLInputElement>): void => {
                                setFromNumber(event.currentTarget.value);
                            }}
                            value={fromNumber}
                        />
                    </GridItem>
                    <GridItem xs={6}>
                        <CustomInputExt
                            labelText="pools:sim.range_to_msisdn"
                            onChange={(event: React.FormEvent<HTMLInputElement>): void => {
                                setToNumber(event.currentTarget.value);
                            }}
                            value={toNumber}
                        />
                    </GridItem>
                    <GridItem xs={6}>
                        <SimpleSelect
                            dispatchName={PlatformIccidMsisdnIpPoolReducerAction.MSISDN_OWNER_CHANGE}
                            label="pools:sim.choose_partner"
                            required={false}
                            elements={platformResellerHeader}
                        />
                    </GridItem>
                    <GridItem xs={6}>
                        <div style={{ marginTop: '20px' }}>
                            <I18n trans="pools:sim.total_numbers" valueObject={{ count: totalCard }} />
                        </div>
                    </GridItem>
                </GridContainer>
            </DialogContent>
            <DialogActions>
                <CustomTextButtom callback={closeCallback} callbackParam={{}} label="general:common.close" size="sm" />
                {selectedValue && fromNumber && (
                    <CustomTextButtom callback={confirmCallback} callbackParam={{}} label="pools:sim.confirm_partner_change" size="sm" />
                )}
            </DialogActions>
        </Dialog>
    );
};
